/* eslint-disable */
import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
  Box,
  Paper,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Typography,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Snackbar,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
  Tab, 
  Tabs,
  TextField
} from "@mui/material";

import { FaExternalLinkAlt } from "react-icons/fa";
import RefreshIcon from "@mui/icons-material/Refresh";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { safeFetch } from "./Safefetch";

const API_BASE = "http://ec2-43-205-136-189.ap-south-1.compute.amazonaws.com";
//const API_BASE ="http://127.0.0.1:8000";


const URLS = {
  list: `${API_BASE}/notifications/state_pdfs/`,
  upload: `${API_BASE}/notifications/state_pdfs/upload/`,
  replace: (id) => `${API_BASE}/notifications/state_pdfs/${id}/replace/`,
  del: (id) => `${API_BASE}/notifications/state_pdfs/${id}/delete/`,
};


const monthNames = [
  "January","February","March","April","May","June",
  "July","August","September","October","November","December",
];

const pad2 = (n) => String(n).padStart(2, "0");

const ymToMonthName = (ym) => {
  if (!ym || typeof ym !== "string" || ym.length < 7) return "—";
  const mm = Number(ym.split("-")[1]);
  if (!mm || mm < 1 || mm > 12) return "—";
  return monthNames[mm - 1];
};

const ensureAbsoluteUrl = (u) => {
  if (!u) return "";
  const s = String(u);
  if (s.startsWith("http://") || s.startsWith("https://")) return s;
  if (s.startsWith("/")) return `${API_BASE}${s}`;
  return s;
};

const fmtNiceDateTime = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString();
};

const buildYearOptions = (rangeBack = 5, rangeForward = 1) => {
  const now = new Date();
  const y = now.getFullYear();
  const arr = [];
  for (let i = y + rangeForward; i >= y - rangeBack; i--) arr.push(i);
  return arr;
};

// Convert (year + monthIndex 0..11) => "YYYY-MM"
const makeYM = (year, monthIndex) => `${year}-${pad2(monthIndex + 1)}`;



export default function ComplianceCalendarPane({ isAdmin = true }) {
  const [activeTab, setActiveTab] = useState(0);

  const [title, setTitle] = useState("");
  const [message, setMessage] = useState("");
  const [scheduleAt, setScheduleAt] = useState("");
  const [savingNotification, setSavingNotification] = useState(false);
  const [snack, setSnack] = useState({ open: false, msg: "", sev: "success" });
  const showSnack = (msg, sev = "success") => setSnack({ open: true, msg, sev });

  // Year options
  const yearOptions = useMemo(() => buildYearOptions(5, 1), []);
  const now = new Date();
  const defaultYear = now.getFullYear();
  const defaultMonthIndex = now.getMonth(); // 0..11

  // Filters (State + Year + MonthName)
  const [filterState, setFilterState] = useState("All");
  const [filterDomain, setFilterDomain] = useState("All");

  const [filterYear, setFilterYear] = useState(String(defaultYear));
  const [filterMonthName, setFilterMonthName] = useState("All"); // "January"... or "All"

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  // Upload dialog states
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadState, setUploadState] = useState("");
  const [uploadYear, setUploadYear] = useState(String(defaultYear));
  const [uploadMonthName, setUploadMonthName] = useState(monthNames[defaultMonthIndex]); // month name only
  const [pdfFile, setPdfFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  
  const [states, setStates] = useState([]);
  const [statesLoading, setStatesLoading] = useState(false);
  const [domains, setDomains] = useState([]);
  const [domainsLoading, setDomainsLoading] = useState(false);
  const [domain, setDomain] = useState("General");

  // Replace mode
  const [replaceId, setReplaceId] = useState(null);

  // Delete
  const [deleteRow, setDeleteRow] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const getSelectedUploadYM = () => {
    const y = Number(uploadYear);
    const mi = monthNames.indexOf(uploadMonthName);
    if (!y || mi < 0) return "";
    return makeYM(y, mi);
  };

  const fetchStates = useCallback(async () => {
    setStatesLoading(true);
    try {
      const res = await safeFetch(`${API_BASE}/docmgmt/list_states/`, {
        headers: getLLHeaders(),});
      const data = await res.json();

      if (!res.ok) throw new Error(data?.error || "Failed to load states");

      // expected formats:
      // ["Maharashtra", "Gujarat", ...]
      // OR [{ name: "Maharashtra" }, ...]
      const normalized = Array.isArray(data)
        ? data.map((s) => (typeof s === "string" ? s : s.name)).filter(Boolean)
        : [];

      setStates(normalized);
    } catch (e) {
      showSnack(e.message || "Failed to load states", "error");
      setStates([]);
    } finally {
      setStatesLoading(false);
    }
  }, []);
  const getLLHeaders = (extra = {}) => {
    const t = localStorage.getItem("labour_law_token");
    return {
      ...extra,
      ...(t ? { Authorization: `Bearer ${t}` } : {}),
    };
  };

  const getSelectedFilterYM = () => {
    if (filterMonthName === "All") return "";
    const y = Number(filterYear);
    const mi = monthNames.indexOf(filterMonthName);
    if (!y || mi < 0) return "";
    return makeYM(y, mi);
  };

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      let url = `${URLS.list}?t=${Date.now()}`;

      const params = [];
      if (filterState !== "All") params.push(`state=${encodeURIComponent(filterState)}`);
      if (filterDomain !== "All") params.push(`domain=${encodeURIComponent(filterDomain)}`);

      const filterYM = getSelectedFilterYM();
      if (filterYM) params.push(`month=${encodeURIComponent(filterYM)}`);

      if (params.length) url = `${URLS.list}?${params.join("&")}&t=${Date.now()}`;

      const res = await safeFetch(url,{headers: getLLHeaders()}, { cache: "no-store" });
      const data = await res.json().catch(() => []);
      if (!res.ok) throw new Error(data?.error || "Failed to load PDFs");

      const normalized = (Array.isArray(data) ? data : []).map((r) => {
        const u = r.url || r.file_url || r.fileUrl || "";
        return { ...r, url: ensureAbsoluteUrl(u) };
      });

      setRows(normalized);
    } catch (e) {
      showSnack(e.message || "Failed to load PDFs", "error");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [filterState, filterYear, filterDomain, filterMonthName]);

  const fetchDomains = useCallback(async () => {
    setDomainsLoading(true);
    try {
      const res = await safeFetch(`${API_BASE}/docmgmt/list_domains/`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("labour_law_token")}`,
        },
      });
      const data = await res.json();

      if (!res.ok) throw new Error(data?.error || "Failed to load domains");

      const normalized = Array.isArray(data)
        ? data.map((d) => (typeof d === "string" ? d : d.name)).filter(Boolean)
        : [];

      setDomains(normalized);

      // fallback safety
      if (!normalized.includes(domain)) {
        setDomain(normalized[0] || "General");
      }
    } catch (e) {
      showSnack(e.message || "Failed to load domains", "error");
      setDomains([]);
    } finally {
      setDomainsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);
  useEffect(() => {
    fetchStates();
  }, [fetchStates]);
  useEffect(() => {
    fetchDomains();
  }, [fetchDomains]);


    

  const openUploadDialog = () => {
    setReplaceId(null);
    setUploadState("");
    setUploadYear(String(defaultYear));
    setUploadMonthName(monthNames[defaultMonthIndex]);
    setPdfFile(null);
    setUploadOpen(true);
  };

  const openReplaceDialog = (row) => {
    setReplaceId(row.id);
    setUploadState(row.state || "");
    setDomain(row.domain || "");
    

    // row.month_ym = "YYYY-MM" => map to year + month name
    const ym = row.month_ym || "";
    const parts = ym.split("-");
    const yr = parts?.[0] || String(defaultYear);
    const monthNm = ymToMonthName(ym);

    setUploadYear(String(yr));
    setUploadMonthName(monthNm !== "—" ? monthNm : monthNames[defaultMonthIndex]);
    setPdfFile(null);
    setUploadOpen(true);
  };

  const submitUploadOrReplace = async () => {
    const st = (uploadState || "").trim();
    const ym = getSelectedUploadYM();

    if (!st) return showSnack("Please select State.", "error");
    if (!ym) return showSnack("Please select Year and Month.", "error");
    if (!pdfFile) return showSnack("Please choose a PDF.", "error");
    if (!String(pdfFile.name || "").toLowerCase().endsWith(".pdf")) return showSnack("Only PDF files are allowed.", "error");

    setSubmitting(true);
    try {
      const form = new FormData();
      form.append("state", st);
      form.append("domain", domain);
      form.append("month_ym", ym); // ✅ backend still gets YYYY-MM
      form.append("file", pdfFile);

      const endpoint = replaceId ? URLS.replace(replaceId) : URLS.upload;

      const res = await safeFetch(endpoint, {
        method: "POST",
        headers: getLLHeaders(),   // ✅ only Authorization
        body: form,
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || "Save failed");

      showSnack(replaceId ? "PDF replaced successfully." : "PDF uploaded successfully.", "success");
      setUploadOpen(false);
      setPdfFile(null);
      setReplaceId(null);
      await fetchRows();
    } catch (e) {
      showSnack(e.message || "Save failed", "error");
    } finally {
      setSubmitting(false);
    }
  };

  
  const confirmDelete = async () => {
    if (!deleteRow?.id) return;

    setDeleting(true);
    try {
      const res = await safeFetch(URLS.del(deleteRow.id), {
        method: "DELETE",
        headers: getLLHeaders(),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || "Delete failed");

      showSnack("PDF deleted.", "success");
      setDeleteRow(null);
      await fetchRows();
    } catch (e) {
      showSnack(e.message || "Delete failed", "error");
    } finally {
      setDeleting(false);
    }
  };
  const createScheduledNotification = async () => {
  if (!title || !message || !scheduleAt) {
    showSnack("All fields are required", "error");
    return;
  }

  setSavingNotification(true);

    try {
      const res = await safeFetch(`${API_BASE}/notifications/admin_create_notification/`, {
        method: "POST",
        headers: getLLHeaders({ "Content-Type": "application/json" }),
        body: JSON.stringify({
          title,
          body: message,
          tag: "Compliance",
          schedule_at: new Date(scheduleAt).toISOString(),
          source_type: "calendar",
        }),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || "Failed to create notification");

      showSnack("Notification scheduled successfully", "success");

      // reset form
      setTitle("");
      setMessage("");
      setScheduleAt("");

    } catch (err) {
      showSnack(err.message || "Failed to save notification", "error");
    } finally {
      setSavingNotification(false);
    }
  };

  return (
    <Box sx={{ p: 3, bgcolor: "#f5f5f7", minHeight: "100%" }}>
      <Tabs
        value={activeTab}
        onChange={(e, v) => setActiveTab(v)}
        sx={{ mb: 2 }}
      >
        <Tab label="Compliance PDFs" />
        <Tab label="Scheduled Notifications" />
      </Tabs>
      {activeTab === 0 && (
        <>
          <Typography variant="h5" sx={{ fontWeight: 800, mb: 0.5 }}>
            Compliance PDFs
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Upload PDF with State + Month. Users can open it anytime.
          </Typography>

          <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: "1px solid #e5e7eb", bgcolor: "#fff" }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={4}>
                <FormControl fullWidth size="small">
                  <InputLabel>Filter State</InputLabel>
                  <Select label="Filter State" value={filterState} onChange={(e) => setFilterState(e.target.value)} disabled={statesLoading}>
                    <MenuItem value="All">All States</MenuItem>
                    {states.map((st) => (
                      <MenuItem key={st} value={st}>{st}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Domain</InputLabel>
                  <Select
                    label="Filter Domain"
                    value={filterDomain}
                    onChange={(e) => setFilterDomain(e.target.value)}
                    disabled={domainsLoading}   // ✅ IMPORTANT
                  >
                    {domainsLoading ? (
                      <MenuItem disabled>Loading domains…</MenuItem>
                    ) : (
                      domains.map((d) => (
                        <MenuItem key={d} value={d}>{d}</MenuItem>
                      ))
                    )}
                  </Select>
                </FormControl>
              </Grid>


              {/* ✅ Filter Year */}
              <Grid item xs={6} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Year</InputLabel>
                  <Select label="Year" value={filterYear} onChange={(e) => setFilterYear(e.target.value)}>
                    {yearOptions.map((y) => (
                      <MenuItem key={y} value={String(y)}>{y}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* ✅ Filter Month (names only) */}
              <Grid item xs={6} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Month</InputLabel>
                  <Select label="Month" value={filterMonthName} onChange={(e) => setFilterMonthName(e.target.value)}>
                    <MenuItem value="All">All Months</MenuItem>
                    {monthNames.map((m) => (
                      <MenuItem key={m} value={m}>{m}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={4}>
                <Box sx={{ display: "flex", gap: 1, justifyContent: { xs: "flex-start", md: "flex-end" } }}>
                  <Button
                    variant="outlined"
                    startIcon={<RefreshIcon />}
                    onClick={fetchRows}
                    disabled={loading}
                    sx={{ textTransform: "none" }}
                  >
                    Refresh
                  </Button>

                  {isAdmin ? (
                    <Button
                      variant="contained"
                      startIcon={<UploadFileIcon />}
                      onClick={openUploadDialog}
                      sx={{ textTransform: "none" }}
                    >
                      Upload PDF
                    </Button>
                  ) : null}
                </Box>
              </Grid>
            </Grid>

            <Box sx={{ mt: 2 }}>
              {loading ? (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 3 }}>
                  <CircularProgress size={18} />
                  <Typography variant="body2" color="text.secondary">Loading…</Typography>
                </Box>
              ) : rows.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                  No PDFs found.
                </Typography>
              ) : (
                <TableContainer sx={{ mt: 1, border: "1px solid #eef2f7", borderRadius: 1 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>State</TableCell>
                        <TableCell>Month</TableCell>
                        <TableCell>File</TableCell>
                        <TableCell>Open</TableCell>
                        <TableCell>Updated</TableCell>
                        <TableCell align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {rows.map((r) => (
                        <TableRow key={r.id} hover>
                          <TableCell sx={{ fontWeight: 800 }}>{r.state}</TableCell>

                          {/* ✅ Show month name only */}
                          <TableCell sx={{ fontWeight: 800 }}>{ymToMonthName(r.month_ym)}</TableCell>

                          <TableCell>{r.file_name || "PDF"}</TableCell>

                          <TableCell>
                            {r.url ? (
                              <Button
                                href={r.url}
                                target="_blank"
                                rel="noopener"
                                size="small"
                                endIcon={<FaExternalLinkAlt style={{ fontSize: 11 }} />}
                                sx={{ textTransform: "none" }}
                              >
                                Open
                              </Button>
                            ) : (
                              <Typography variant="caption" color="text.secondary">—</Typography>
                            )}
                          </TableCell>

                          <TableCell>{fmtNiceDateTime(r.updated_at)}</TableCell>

                          <TableCell align="right">
                            {isAdmin ? (
                              <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, flexWrap: "wrap" }}>
                                <Button
                                  variant="outlined"
                                  size="small"
                                  onClick={() => openReplaceDialog(r)}
                                  sx={{ textTransform: "none" }}
                                >
                                  Replace
                                </Button>

                                <Tooltip title="Delete">
                                  <IconButton color="error" onClick={() => setDeleteRow(r)}>
                                    <DeleteOutlineIcon />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            ) : (
                              <Typography variant="caption" color="text.secondary">Read-only</Typography>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          </Paper>

          {/* ✅ Upload / Replace Dialog (Month name only + Year) */}
          <Dialog open={uploadOpen} onClose={() => (!submitting ? setUploadOpen(false) : null)} maxWidth="sm" fullWidth>
            <DialogTitle>{replaceId ? "Replace PDF" : "Upload PDF"}</DialogTitle>

            <DialogContent dividers>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>State</InputLabel>
                    <Select
                      label="State"
                      value={uploadState}
                      onChange={(e) => setUploadState(e.target.value)}
                      disabled={statesLoading || submitting}
                    >
                      {states.map((st) => (
                        <MenuItem key={st} value={st}>{st}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Domain</InputLabel>
                    <Select
                      label="Domain"
                      value={domain}
                      onChange={(e) => setDomain(e.target.value)}
                      disabled={domainsLoading || submitting}
                    >
                      {domainsLoading ? (
                        <MenuItem disabled>Loading domains…</MenuItem>
                      ) : (
                        domains.map((d) => (
                          <MenuItem key={d} value={d}>
                            {d}
                          </MenuItem>
                        ))
                      )}
                    </Select>
                  </FormControl>
                </Grid>

                        
                {/* ✅ Year */}
                <Grid item xs={6} md={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Year</InputLabel>
                    <Select
                      label="Year"
                      value={uploadYear}
                      onChange={(e) => setUploadYear(e.target.value)}
                      disabled={submitting}
                    >
                      {yearOptions.map((y) => (
                        <MenuItem key={y} value={String(y)}>{y}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                {/* ✅ Month (names only) */}
                <Grid item xs={6} md={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Month</InputLabel>
                    <Select
                      label="Month"
                      value={uploadMonthName}
                      onChange={(e) => setUploadMonthName(e.target.value)}
                      disabled={submitting}
                    >
                      {monthNames.map((m) => (
                        <MenuItem key={m} value={m}>{m}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12}>
                  <Button
                    variant="outlined"
                    component="label"
                    startIcon={<UploadFileIcon />}
                    disabled={submitting}
                    sx={{ textTransform: "none" }}
                  >
                    Choose PDF
                    <input
                      type="file"
                      accept="application/pdf"
                      hidden
                      onChange={(e) => setPdfFile(e.target.files?.[0] || null)}
                    />
                  </Button>

                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    {pdfFile ? pdfFile.name : "No file selected"}
                  </Typography>
                </Grid>
              </Grid>
            </DialogContent>

            <DialogActions>
              <Button
                onClick={() => {
                  if (submitting) return;
                  setUploadOpen(false);
                  setPdfFile(null);
                  setReplaceId(null);
                }}
                sx={{ textTransform: "none" }}
              >
                Cancel
              </Button>

              <Button
                variant="contained"
                disabled={submitting}
                onClick={submitUploadOrReplace}
                sx={{ textTransform: "none" }}
              >
                {submitting ? "Saving..." : replaceId ? "Replace" : "Upload"}
              </Button>
            </DialogActions>
          </Dialog>

          {/* Delete Confirm */}
          <Dialog open={!!deleteRow} onClose={() => (!deleting ? setDeleteRow(null) : null)} maxWidth="xs" fullWidth>
            <DialogTitle>Delete PDF</DialogTitle>
            <DialogContent dividers>
              <Typography variant="body2">
                Delete: <b>{deleteRow?.file_name}</b> ?
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                {deleteRow?.state} - {ymToMonthName(deleteRow?.month_ym)}
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => (!deleting ? setDeleteRow(null) : null)} sx={{ textTransform: "none" }}>
                Cancel
              </Button>
              <Button
                variant="contained"
                color="error"
                onClick={confirmDelete}
                disabled={deleting}
                sx={{ textTransform: "none" }}
              >
                {deleting ? "Deleting..." : "Delete"}
              </Button>
            </DialogActions>
          </Dialog>
        </>
      )}
      {activeTab === 1 && (
        <Paper sx={{ p: 3, borderRadius: 2 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Schedule Compliance Notification
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                label="Title"
                fullWidth
                value={title}
                onChange={(e) => setTitle(e.target.value)}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="Message"
                fullWidth
                multiline
                minRows={3}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
              />
            </Grid>

            <Grid item xs={6}>
              <TextField
                type="datetime-local"
                label="Schedule At"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={scheduleAt}
                onChange={(e) => setScheduleAt(e.target.value)}
              />
            </Grid>

            <Grid item xs={12}>
              <Button
                variant="contained"
                disabled={savingNotification}
                onClick={createScheduledNotification}
              >
                {savingNotification ? "Saving..." : "Save Notification"}
              </Button>
            </Grid>
          </Grid>
        </Paper>
      )}


      <Snackbar open={snack.open} autoHideDuration={3000} onClose={() => setSnack((s) => ({ ...s, open: false }))}>
        <Alert severity={snack.sev} onClose={() => setSnack((s) => ({ ...s, open: false }))} sx={{ width: "100%" }}>
          {snack.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}
